import { flow, isUndefined, set, uniq } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { DEFAULT_PAGE_SIZE } from '../../../constants/arkTable';
import { AnalysisView } from '../../../documents/store/types';
import { NettingPage, OpinionNettingEngineState, OpinionNettingEngineActionTypes, NettingEngineTableView, SelectedJurisdictionDetails, ISDANettingEngineData } from './types';
import { MyDocumentActionTypes } from '../../../documents/my-documents/store/types';
import { updateNettingAnalysis } from '../../../documents/store/utils';

export const initialOpinionReportFilter = {
    jurisdiction: null,
    focus: null
};

export const initialNettingEngineData: ISDANettingEngineData = {
    counterpartyNameKnown: null,
    counterparty: { entityId: null, jurisdiction: null, counterpartyClassification: null, subCounterpartyTypeId: null },
    agreement: null,
    governingLaw: null,
    productTypesKnown: null,
    productsTraded: [],
    counterpartyIsMultiBranch: null,
    counterpartyOffices: []
};

export const initialSelectedJurisdictionDetails: SelectedJurisdictionDetails = {
    question: '',
    value: '',
    details: null,
    icon: ''
};

export const INITIAL_STATE: OpinionNettingEngineState = {
    nettingPage: NettingPage.SELECT,
    currentNettingEngineData: initialNettingEngineData,
    savedNettingEngineData: null,
    engineResults: null,
    theoreticalQuestion: null,
    calculatingResults: false,
    surveyQuestionIndex: 0,
    showSummary: false,
    leiModalOpen: false,
    isChangingQuestion: false,
    nettingAssumptionsModalOpen: false,
    enquiredOpinions: [],
    isCustomConclusion: false,
    nettingEngineDocumentFilters: {},
    nettingEngineDocumentSort: undefined,
    nettingEngineDocumentPageSize: DEFAULT_PAGE_SIZE,
    isLoading: false,
    nettingEngineDocuments: [],
    totalNettingEngineDocuments: 0,
    nettingEngineDocumentPage: 1,
    isOpening: false,
    nettingEngineTableView: NettingEngineTableView.EXISTING,
    documentSummaryView: AnalysisView.DATASET,
    selectedProductCovered: null,
    selectedJurisdictionDetails: initialSelectedJurisdictionDetails,
    answerJustificationModalOpen: false,
    isDownloadingNettingSummary: false
};

export const opinionNettingEngineReducer: Reducer<OpinionNettingEngineState> = (state = INITIAL_STATE, { payload, type }): OpinionNettingEngineState => {
    switch (type) {
        case OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_PAGE:
            return set('nettingPage', payload, state);
        case OpinionNettingEngineActionTypes.RESET_NETTING_ENGINE_PAGE:
            return set('nettingPage', NettingPage.SELECT, state);
        case OpinionNettingEngineActionTypes.UPDATE_NETTING_ENGINE_DATA: {
            const { key, value, counterpartyKey } = payload;
            if (!isUndefined(counterpartyKey)) {
                if (counterpartyKey === 'entityId') {
                    return flow(
                        set(`currentNettingEngineData[${key}][${counterpartyKey}]`, value),
                        set(`currentNettingEngineData[${key}][jurisdiction]`, null),
                        set(`currentNettingEngineData[${key}][counterpartyClassification]`, null)
                    )(state);
                }
                return set(`currentNettingEngineData[${key}][${counterpartyKey}]`, value, state);
            }
            if (key === 'productTypesKnown' && !value) {
                return flow(
                    set(`currentNettingEngineData[${key}]`, value),
                    set('currentNettingEngineData.productsTraded', [])
                )(state);
            }
            if (key === 'counterpartyIsMultiBranch' && !value) {
                return flow(
                    set(`currentNettingEngineData[${key}]`, value),
                    set('currentNettingEngineData.counterpartyOffices', [])
                )(state);
            }
            return set(`currentNettingEngineData[${key}]`, value, state);
        }
        case OpinionNettingEngineActionTypes.CALCULATE_NETTING_ENGINE_STARTED:
            return set('calculatingResults', true, state);
        case OpinionNettingEngineActionTypes.CALCULATE_NETTING_ENGINE_SUCCESSFUL:
            return flow(
                set('engineResults', payload),
                set('isOpening', false),
                set('savedNettingEngineData', state.currentNettingEngineData),
                set('currentNettingEngineData', state.currentNettingEngineData),
                set('calculatingResults', false)
            )(state);
        case OpinionNettingEngineActionTypes.CALCULATE_NETTING_ENGINE_FAILED:
            return set('calculatingResults', false, state);
        case OpinionNettingEngineActionTypes.SET_SURVEY_QUESTION_INDEX:
            return set('surveyQuestionIndex', payload, state);
        case OpinionNettingEngineActionTypes.RESET_ENGINE_DATA_STATE:
            return flow(
                set('currentNettingEngineData', initialNettingEngineData),
                set('savedNettingEngineData', null),
                set('engineResults', null),
                set('theoreticalQuestion', null),
                set('calculatingResults', false),
                set('surveyQuestionIndex', 0),
                set('showSummary', false),
                set('enquiredOpinions', []),
                set('isCustomConclusion', false)
            )(state);
        case OpinionNettingEngineActionTypes.SET_SHOW_SUMMARY:
            return set('showSummary', payload, state);
        case OpinionNettingEngineActionTypes.SET_LEI_MODAL_OPEN:
            return set('leiModalOpen', payload, state);
        case OpinionNettingEngineActionTypes.SET_CHANGING_QUESTION:
            return set('isChangingQuestion', payload, state);
        case OpinionNettingEngineActionTypes.SET_NETTING_ASSUMPTIONS_MODAL_OPEN:
            return set('nettingAssumptionsModalOpen', payload, state);
        case OpinionNettingEngineActionTypes.ENQUIRE_NETTING_OPINION_SUCCESSFUL:
            return set('enquiredOpinions', uniq([...state.enquiredOpinions, ...payload]), state);
        case OpinionNettingEngineActionTypes.ENQUIRE_NETTING_OPINION_FAILED:
            return set('enquiredOpinions', [], state);
        case OpinionNettingEngineActionTypes.TOGGLE_INDUSTRY_OR_CUSTOM_CONCLUSIONS:
            return set('isCustomConclusion', payload, state);
        case OpinionNettingEngineActionTypes.FETCH_NETTING_ENGINE_DOCUMENTS_STARTED:
            return set('isLoading', true, state);
        case OpinionNettingEngineActionTypes.FETCH_NETTING_ENGINE_DOCUMENTS_SUCCESSFUL:
            return flow(
                set('nettingEngineDocuments', payload.documents),
                set('totalNettingEngineDocuments', payload.total || state.totalNettingEngineDocuments),
                set('nettingEngineDocumentPage', payload.pageNumber || state.nettingEngineDocumentPage),
                set('isLoading', false)
            )(state);
        case OpinionNettingEngineActionTypes.FETCH_NETTING_ENGINE_DOCUMENTS_FAILED:
            return set('isLoading', false, state);
        case OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS: {
            const isTextFilter = payload.type === 'text';
            return flow(
                set(`nettingEngineDocumentFilters[${payload.key}][${payload.type}]`, payload.value),
                set(`nettingEngineDocumentFilters[${payload.key}][${isTextFilter ? 'dropdown' : 'text'}]`, isTextFilter ? null : '')
            )(state);
        }
        case OpinionNettingEngineActionTypes.CLEAR_NETTING_ENGINE_DOCUMENT_TABLE_FILTERS:
            return set('nettingEngineDocumentFilters', {}, state);
        case OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENT_TABLE_COLUMN_SORT:
            return set('nettingEngineDocumentSort', payload, state);
        case OpinionNettingEngineActionTypes.SET_NETTING_ENGINE_DOCUMENTS_PAGE_SIZE:
            return set('nettingEngineDocumentPageSize', payload, state);
        case OpinionNettingEngineActionTypes.OPEN_NETTING_ENGINE_DOCUMENT_STARTED:
            return set('isOpening', true, state);
        case OpinionNettingEngineActionTypes.OPEN_NETTING_ENGINE_DOCUMENT_SUCCESSFUL: {
            const { document, allDataCompletedForInstantCalculation } = payload;
            return flow(
                set('isOpening', allDataCompletedForInstantCalculation),
                set('savedNettingEngineData', document),
                set('currentNettingEngineData', document)
            )(state);
        }
        case OpinionNettingEngineActionTypes.OPEN_NETTING_ENGINE_DOCUMENT_FAILED:
            return set('isOpening', false, state);
        case OpinionNettingEngineActionTypes.UPDATE_NETTING_ENGINE_FIELDS: {
            const { key, value, counterpartyKey } = payload;
            if (!value && ['productTypesKnown', 'counterpartyIsMultiBranch'].includes(key)) {
                // If a user marks either the productTypesKnown or counterpartyIsMultiBranch boolean dropdowns as "No" or null, we need to clear any previously selected values
                const secondKey = key === 'counterpartyIsMultiBranch' ? 'counterpartyOffices' : 'productsTraded';
                return flow(
                    set(`currentNettingEngineData[${key}]`, value),
                    set(`currentNettingEngineData[${secondKey}]`, [])
                )(state);
            }
            const path = counterpartyKey ? `currentNettingEngineData[${key}][${counterpartyKey}]` : `currentNettingEngineData[${key}]`;
            return set(path, value, state);
        }
        case OpinionNettingEngineActionTypes.TOGGLE_NETTING_ENGINE_VIEW:
            return set('nettingEngineTableView', payload, state);
        case OpinionNettingEngineActionTypes.TOGGLE_DOCUMENT_SUMMARY_VIEW:
            return set('documentSummaryView', payload, state);
        case OpinionNettingEngineActionTypes.SET_SELECTED_PRODUCT_COVERED:
            return set('selectedProductCovered', payload, state);
        case OpinionNettingEngineActionTypes.SET_SELECTED_JURISDICTION_DETAILS:
            return set('selectedJurisdictionDetails', payload, state);
        case OpinionNettingEngineActionTypes.SET_ANSWER_JUSTIFICATION_MODAL_OPEN:
            return set('answerJustificationModalOpen', payload, state);
        case OpinionNettingEngineActionTypes.DOWNLOAD_NETTING_ENGINE_RESULTS_STARTED:
            return set('isDownloadingNettingSummary', true, state);
        case OpinionNettingEngineActionTypes.DOWNLOAD_NETTING_ENGINE_RESULTS_SUCCESSFUL:
        case OpinionNettingEngineActionTypes.DOWNLOAD_NETTING_ENGINE_RESULTS_FAILED:
            return set('isDownloadingNettingSummary', false, state);
        case MyDocumentActionTypes.DOCUMENT_NETTING_ANALYSIS_COMPLETE: {
            const { documentId, canBeCalculated, nettingAnalysis } = payload;
            const updatedNettingEngineDocuments = state.nettingEngineDocuments.map(originalDocument => originalDocument.documentId === documentId ? updateNettingAnalysis(originalDocument, canBeCalculated, nettingAnalysis) : originalDocument);
            return set('nettingEngineDocuments', updatedNettingEngineDocuments, state);
        }
        case MyDocumentActionTypes.TOGGLE_DOCUMENT_NETTING_ANALYSIS_OPEN: {
            if (!payload) {
                return flow(
                    set('savedNettingEngineData', null),
                    set('currentNettingEngineData', initialNettingEngineData),
                    set('engineResults', null),
                    set('calculatingResults', false)
                )(state);
            }
            return state;
        }
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
